/**
 * Computes the current academic year and semester based on the current date.
 *
 * Academic calendar:
 * - 1st Semester: August – December
 * - 2nd Semester: January – May
 * - Summer: June – July
 * - Academic year runs from August to July (e.g., Aug 2025 – Jul 2026 = "2025-2026")
 */
export function getCurrentAcademicPeriod(date: Date = new Date()) {
  const month = date.getMonth() + 1; // 1-indexed
  const year = date.getFullYear();

  let academicYearStart: number;
  let semester: number;
  let semesterLabel: string;

  if (month >= 8) {
    // August–December: 1st Semester of Academic Year YYYY-(YYYY+1)
    academicYearStart = year;
    semester = 1;
    semesterLabel = '1st Semester';
  } else if (month >= 6) {
    // June–July: Summer of Academic Year (YYYY-1)-YYYY
    academicYearStart = year - 1;
    semester = 3;
    semesterLabel = 'Summer';
  } else {
    // January–May: 2nd Semester of Academic Year (YYYY-1)-YYYY
    academicYearStart = year - 1;
    semester = 2;
    semesterLabel = '2nd Semester';
  }

  const academicYear = `${academicYearStart}-${academicYearStart + 1}`;

  return { academicYear, semester, semesterLabel };
}

export function normalizeAcademicYear(value?: string | null): string {
  if (!value) return '';

  const match = value.trim().match(/^(\d{4})\s*-\s*(\d{4})$/);
  if (!match) {
    return '';
  }

  const startYear = Number(match[1]);
  const endYear = Number(match[2]);

  if (endYear !== startYear + 1) {
    return '';
  }

  return `${startYear}-${endYear}`;
}

export function normalizeAcademicYearList(values: string[] | string | null | undefined): string[] {
  const rawValues = Array.isArray(values) ? values : typeof values === 'string' ? values.split(/[\n,]+/) : [];

  return Array.from(
    new Set(
      rawValues
        .map((value) => normalizeAcademicYear(value))
        .filter((value): value is string => Boolean(value))
    )
  ).sort((left, right) => left.localeCompare(right));
}
